import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

const hexToARGB = (hex) => {
	let h = hex.replace("#", "");
	if (h.length === 3)
		h = h
			.split("")
			.map((c) => c + c)
			.join("");
	return `FF${h.toUpperCase()}`;
};

const mapMuiSxToExcelStyle = (sx = {}) => {
	const style = { alignment: { horizontal: "center", vertical: "center" } };

	if (sx.fontWeight === "bold") {
		style.font = { ...style.font, bold: true };
	}

	if (sx.fontSize && typeof sx.fontSize === "number") {
		style.font = { ...style.font, size: sx.fontSize };
	}

	if (sx.color) {
		style.font = { ...style.font, color: { argb: hexToARGB(sx.color) } };
	}

	if (sx.backgroundColor || sx.background) {
		style.fill = {
			type: "pattern",
			pattern: "solid",
			fgColor: { argb: hexToARGB(sx.backgroundColor ?? sx.background) },
		};
	}

	if (sx.textAlign) {
		style.alignment = { ...style.alignment, horizontal: sx.textAlign };
	}

	if (sx.verticalAlign) {
		style.alignment = { ...style.alignment, vertical: sx.verticalAlign };
	}
	return style;
};

export const exportStyledExcelMultipleTables = async ({ filename = "CombinedTables.xlsx", sheetName = "All Tables", tables = [] }) => {
	const workbook = new ExcelJS.Workbook();
	const worksheet = workbook.addWorksheet(sheetName);
	let currentRow = 1;

	for (const { title, columns, rows } of tables) {
		const titleCell = worksheet.getCell(`A${currentRow}`);
		titleCell.value = title || "Table";
		titleCell.font = { bold: true, size: 14 };
		worksheet.mergeCells(`A${currentRow}:${String.fromCharCode(65 + columns.length - 1)}${currentRow}`);
		currentRow++;

		const headerRow = worksheet.insertRow(
			currentRow++,
			columns.map((col) => col.name)
		);
		columns.forEach((col, colIdx) => {
			const cell = headerRow.getCell(colIdx + 1);
			const style = mapMuiSxToExcelStyle(col.headerSx);
			Object.assign(cell, style);
		});

		for (const rowData of rows) {
			const rowValues = columns.map((col) =>
				typeof col.valueGetter === "function" ? col.valueGetter(rowData) : rowData[col.key] ?? ""
			);
			const dataRow = worksheet.insertRow(currentRow++, rowValues);

			columns.forEach((col, colIdx) => {
				const cell = dataRow.getCell(colIdx + 1);
				const style = mapMuiSxToExcelStyle(col.cellSx);
				Object.assign(cell, style);
			});
		}

		currentRow += 2;
	}

	worksheet.columns.forEach((col) => {
		let max = 10;
		col.eachCell?.((cell) => {
			max = Math.max(max, String(cell.value || "").length);
		});
		col.width = max / 2;
	});

	const buffer = await workbook.xlsx.writeBuffer();
	saveAs(
		new Blob([buffer], {
			type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
		}),
		filename
	);
};

export const exportStyledExcelMultipleSheetsWithTables = async ({ filename = "MultiSheetTables.xlsx", sheets = [] }) => {
	const workbook = new ExcelJS.Workbook();

	for (const { sheetName, tables } of sheets) {
		const worksheet = workbook.addWorksheet(sheetName || "Sheet");
		let currentRow = 1;

		for (const { title, columns, rows } of tables) {
			const titleCell = worksheet.getCell(`A${currentRow}`);
			titleCell.value = title || "Table";
			titleCell.font = { bold: true, size: 14 };

			worksheet.mergeCells(`A${currentRow}:${String.fromCharCode(65 + columns.length - 1)}${currentRow}`);
			currentRow++;

			const headerRow = worksheet.insertRow(
				currentRow++,
				columns.map((col) => col.name)
			);
			columns.forEach((col, colIdx) => {
				const cell = headerRow.getCell(colIdx + 1);
				const style = mapMuiSxToExcelStyle(col.headerSx);
				Object.assign(cell, style);
			});

			for (const rowData of rows) {
				const rowValues = columns.map((col) =>
					typeof col.valueGetter === "function" ? col.valueGetter(rowData) : rowData[col.key] ?? ""
				);
				const dataRow = worksheet.insertRow(currentRow++, rowValues);

				columns.forEach((col, colIdx) => {
					const cell = dataRow.getCell(colIdx + 1);
					const style = mapMuiSxToExcelStyle(col.cellSx);
					Object.assign(cell, style);
				});
			}

			currentRow += 2; // space between tables
		}

		// Auto width for columns
		worksheet.columns.forEach((col) => {
			let max = 10;
			col.eachCell?.((cell) => {
				max = Math.max(max, String(cell.value || "").length);
			});
			col.width = max + 2;
		});
	}

	const buffer = await workbook.xlsx.writeBuffer();
	saveAs(
		new Blob([buffer], {
			type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
		}),
		filename
	);
};
